import type { ValidationConfig } from '../../../core/dataProcessor/types';

// 定义股票数据接口
interface Stock {
  code?: string;
  name?: string;
  market?: string;
}

// 🆕 为新 ValidationEngine 导出的配置
export const hxKLineAndDynamicLineValidationConfig: ValidationConfig = {
  fields: [
    { field: 'x', type: 'date', required: true },
    { field: 'y', type: 'number', required: true }
  ],
  businessRules: [
    {
      name: 'hx-kline-and-dynamic-line-stocks-required',
      validator: (data, layer) => {
        const hxKLineLayer = layer as { stocks?: Stock[] };
        return Array.isArray(hxKLineLayer.stocks) && hxKLineLayer.stocks.length > 0;
      },
      message: 'K-line and dynamic line chart requires stock configuration',
      suggestion: 'Provide stocks array with at least one valid stock configuration',
      severity: 'error'
    },
    {
      name: 'hx-kline-and-dynamic-line-stock-properties',
      validator: (data, layer) => {
        const hxKLineLayer = layer as { stocks?: Stock[] };
        if (!Array.isArray(hxKLineLayer.stocks)) {
          return true;
        }

        return hxKLineLayer.stocks.every((stock: Stock) => Boolean(stock.code && stock.market));
      },
      message: 'All stocks must have valid code and market properties',
      suggestion: 'Ensure each stock has both code and market properties defined',
      severity: 'error'
    },
    {
      name: 'hx-kline-and-dynamic-line-sufficient-data',
      validator: data => Array.isArray(data) && data.length >= 2,
      message: 'K-line and dynamic line chart requires at least 2 data points',
      suggestion: 'Provide at least 2 data points for meaningful time series visualization',
      severity: 'warning'
    },
    {
      name: 'hx-kline-and-dynamic-line-valid-dates',
      validator: (data, layer) => {
        const { encoding } = layer;
        if (!Array.isArray(data) || data.length === 0) {
          return true;
        }

        const dates = data.map(
          (item: Record<string, unknown>) => new Date(item[encoding?.x as string] as string)
        );
        return dates.every((date: Date) => !Number.isNaN(date.getTime()));
      },
      message: 'K-line chart requires valid date format in x field',
      suggestion: 'Ensure all x-field values are valid dates (ISO format recommended)',
      severity: 'error'
    },
    {
      name: 'hx-kline-and-dynamic-line-y-field-structure',
      validator: (data, layer) => {
        const { encoding } = layer;
        if (!Array.isArray(data) || data.length === 0) {
          return true;
        }

        // 检查y字段是否为数组或数值
        return data.every((item: Record<string, unknown>) => {
          const yValue = item[encoding?.y as string];
          return typeof yValue === 'number' || Array.isArray(yValue);
        });
      },
      message: 'Dynamic line y field must be number or array type',
      suggestion:
        'Ensure y field contains numerical values or arrays of numbers for multi-line support',
      severity: 'warning'
    }
  ]
};
